import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SegmentedControl, Stack, Text } from "@mantine/core";
import { useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { getSettingsState, setSetting } from "../../reducers/settingReducer.js";
import { useAppAction, useAppSelector } from "../../hooks/redux.js";
import { useCaptureEvent } from "../../components/StyleEditorContext.js";
import { Event } from "../../constants/events.js";
import { isEqual } from "es-toolkit/compat";
import { ExpressionInput } from "../../components/ExpressionInput.js";
const GLOBE_EXPRESSION = [
    "interpolate",
    ["linear"],
    ["zoom"],
    10, "vertical-perspective",
    12, "mercator",
];
const DEFAULT_INTERPOLATE_EXPRESSION = [
    "interpolate",
    ["linear"],
    ["zoom"],
    9, "vertical-perspective",
    12, "mercator",
];
export function deriveProjectionMode(projection) {
    const { type = "mercator" } = projection ?? {};
    if (type === "mercator")
        return "mercator";
    if (type === "globe")
        return "globe";
    return isEqual(type, GLOBE_EXPRESSION)
        ? "globe"
        : "interpolate";
}
export const ProjectionConfig = () => {
    const captureEvent = useCaptureEvent();
    const settings = useAppSelector(getSettingsState);
    const setSettingsAction = useAppAction(setSetting);
    const { projection } = settings;
    const mode = deriveProjectionMode(projection);
    const handleModeChange = useCallback((value) => {
        const newMode = value;
        captureEvent(Event.ChangeProjection, { mode: newMode });
        switch (newMode) {
            case "mercator":
                setSettingsAction({ projection: { type: "mercator" } });
                break;
            case "globe":
                setSettingsAction({ projection: { type: GLOBE_EXPRESSION } });
                break;
            case "interpolate": {
                setSettingsAction({ projection: { type: DEFAULT_INTERPOLATE_EXPRESSION } });
                break;
            }
        }
    }, [captureEvent, setSettingsAction, projection?.type]);
    const handleExpressionChange = useCallback((value) => {
        if (!value)
            return;
        captureEvent(Event.ChangeProjection, { mode: "interpolate" });
        setSettingsAction({ projection: { type: value } });
    }, [captureEvent, setSettingsAction]);
    return (_jsxs(Stack, { w: "100%", gap: "xs", children: [_jsx(Text, { fz: "sm", fw: "bold", children: _jsx(FormattedMessage, { id: "label.projection", defaultMessage: "Projection" }) }), _jsx(SegmentedControl, { size: "xs", value: mode, onChange: handleModeChange, data: [
                    {
                        label: _jsx(FormattedMessage, { id: "label.projection.mercator", defaultMessage: "Mercator" }),
                        value: "mercator",
                    },
                    {
                        label: _jsx(FormattedMessage, { id: "label.projection.globe", defaultMessage: "Globe" }),
                        value: "globe",
                    },
                    {
                        label: _jsx(FormattedMessage, { id: "label.projection.interpolate", defaultMessage: "Interpolate" }),
                        value: "interpolate",
                    },
                ] }), mode === "interpolate" && (_jsx(ExpressionInput, { value: projection?.type, label: null, onChange: handleExpressionChange }))] }));
};
